import React, { Component } from 'react';
import { Container, Row, Col, Card, ButtonGroup, Button, Alert, Form, Badge } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import { IoIosPlay } from "react-icons/io";
import { FaRegStopCircle } from "react-icons/fa";
import { GoX, GoInfo, GoTrash, GoPencil } from "react-icons/go";
import { MdOutlineExpandMore, MdRefresh } from "react-icons/md";
import Navigation from 'context/nav';
import Utils from 'context/utils';
import Clipboard from "../../controls/common/clipboard";
import ActionCard, { ActionCards } from "../../controls/common/actionCard";
import SessionProperties from "../../controls/common/sessionProperties";
import CreateExecutionModal from './create';
import CreateSessionModal from '../sessions/create';
import ConfirmModal from 'components/controls/confirmModal';
import Alias from 'components/controls/alias';
import { callerCellRenderer, callerValueGetter } from 'components/controls/callerCellRenderer';
import { durationCellRenderer, durationValueGetter } from 'components/controls/durationCell';
import {
  SESSION_STATUS, SESSION_CLOSED_HINT, isSessionOpen, isSessionTransitioning
} from "../../playground/session/sessionStatus";
import Preferences from "context/preferences";

// Bounded session-status re-poll after close/delete. Both commands are asynchronous: they return
// immediately with a TRANSITIONAL status ("closing" / "deleting") and the backend reaches the
// terminal state seconds later. 2s between reads keeps the transition feeling live without
// hammering the API; 30 attempts caps a stalled transition at ~60s, after which the poll gives up
// quietly (the Refresh button is still there) rather than spinning for the life of the page.
export const SESSION_POLL_INTERVAL_MS = 2000;
export const SESSION_POLL_MAX_ATTEMPTS = 30;

// SessionActions — the Edit / Close / Reopen / Delete session-lifecycle action cards. Meant to be
// rendered inside the enclosing <ActionCards> frame alongside the "New Execution" card (it renders
// bare ActionCard elements, not its own heading/frame). Hidden entirely when !canEdit. Status
// determines which cards show:
//   open    -> Edit (enabled) + Close (enabled) + Delete (enabled)
//   closing -> Close (disabled, "Closing…") + Delete (disabled)
//   closed  -> Reopen (enabled) + Delete (enabled)
//   deleting-> Delete only (disabled, "Deleting…")
// `busy` additionally disables every visible action while this component's own in-flight request is
// pending (independent of status-driven disablement).
export function SessionActions({ session, canEdit, busy, onClose, onReopen, onDelete, onEdit }) {
  if (!canEdit) return null;

  const status = session ? session.status : null;
  const open = isSessionOpen(session);
  const closing = status === SESSION_STATUS.CLOSING;
  const closed = status === SESSION_STATUS.CLOSED;
  const deleting = status === SESSION_STATUS.DELETING;

  return (
    <>
      {open &&
        <ActionCard helper="Edits the session's alias, description and tracing">
          <Button variant="success" size="sm" disabled={busy} onClick={onEdit}>
            <GoPencil /> Edit Session
          </Button>
        </ActionCard>
      }
      {(open || closing) &&
        <ActionCard helper="Stops executions and makes the session read-only">
          <Button variant="success" size="sm" disabled={busy || closing} onClick={onClose}>
            <FaRegStopCircle /> {closing ? "Closing…" : "Close Session"}
          </Button>
        </ActionCard>
      }
      {closed &&
        <ActionCard helper="Reopens the session so it can run executions">
          <Button variant="success" size="sm" disabled={busy} onClick={onReopen}>
            <IoIosPlay /> Reopen Session
          </Button>
        </ActionCard>
      }
      {session &&
        <ActionCard helper="Deletes the session, its state, and executions">
          <Button variant="danger" size="sm" disabled={busy || closing || deleting} onClick={onDelete}>
            <GoTrash /> {deleting ? "Deleting…" : "Delete Session"}
          </Button>
        </ActionCard>
      }
    </>
  );
}

export default class SessionView extends Component {
  constructor(props) {
    super(props);
    const scope = { orgId: this.props.blob.org_id, blobId: this.props.blob.id };

    this.state = {
      details: Preferences.get("blob:workflow.debuggerDetailsPane", scope),
      definitions: null,
      definitionsIndex: null,
      session: null,
      executions: null,
      lastExecutionId: null,
      createExecutionModelShow: false,
      editSessionModalShow: false,
      progress: false,
      error: false,
      message: null,
      sessionActionBusy: null,
      confirmAction: null,
    };

    // Callers resolved so far, by user id. An instance field rather than state: it is a request-dedupe
    // ledger, not something rendered, and a `null` entry records a lookup that failed so it is not
    // retried on every page.
    this.callersById = {};

    // Status re-poll bookkeeping (see startSessionStatusPoll below). Instance fields rather than
    // state: nothing renders from them.
    this.mounted = false;
    this.sessionPollTimer = null;
    this.sessionPollAttempts = 0;

    // Last label handed to onSessionLoaded (see reportSession below). Instance field rather than
    // state: nothing renders from it.
    this.reportedSessionLabel = null;
  }

  componentDidMount() {
    this.mounted = true;
    this.loadDefinitions();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.stopSessionStatusPoll();
  }

  updateProgress = (progress, error, message) => {
    this.setState({
      progress: progress,
      error: error,
      message: message
    });
  }

  loadDefinitions() {
    // Render progress
    this.updateProgress(true, false, "Loading workflow definitions...");

    // Fetch the list
    const body = {
      engine: "workflow_blobhub",
      command: "list_definitions",
      category: "workflow"
    };
    this.props.app.api.revisionsIdDataQueryPost(this.props.revision.id, body)
      .then( response => {
        // Parse response
        const definitions = response.data.definitions;
        let definitionsIndex = {};
        for ( const definition of definitions ) {
          definitionsIndex[definition["id"]] = definition;
        }

        // Clear progress
        this.updateProgress(false, false, null);

        // Notify the view that definitions have been fetched
        this.setState({
          definitions: definitions,
          definitionsIndex: definitionsIndex
        }, () => {
          this.loadExecutions();
        });
      }, error => {
        this.updateProgress(false, true, "Failed to fetch workflow definitions.");
      });
  }

  /**
   * Loads the executions list and, with it, the session itself (`include_session`).
   *
   * `silent` suppresses every progress/error banner update, which is what the status re-poll needs:
   * setting `progress` true hides the whole Actions section (see the `!this.state.progress` gate in
   * render()), so a visible re-read every couple of seconds would make the actions flicker away.
   *
   * Resolves to `{ session }` on success (session may be absent if it no longer exists) or
   * `{ error }` on failure, so the poll can tell "gone" from "still transitioning" from "transient
   * read failure". Existing callers ignore the resolved value.
   */
  loadExecutions(more, silent) {
    // Render progress
    if ( !silent ) {
      this.updateProgress(true, false, "Loading executions...");
    }

    // Fetch the list
    const sessionId = this.props.match.params.sectionId;
    const body = {
      "engine": "workflow_blobhub",
      "command": "list_executions",
      "session_id": sessionId,
      "include_session": true
    };
    // Page results from where we left off the last time
    if ( more ) {
      body["start_execution_id"] = this.state.lastExecutionId;
    }
    return this.props.app.api.revisionsIdDataQueryPost(this.props.revision.id, body)
      .then( response => {
        // Parse response
        const session = response.data.session;
        const executions = response.data.executions;
        const lastExecutionId = response.data.last_execution_id;
        for ( const execution of executions ) {
          const definitionId = execution["definition_id"];
          if ( this.state.definitionsIndex[definitionId] ) {
            execution["definition_alias"] = this.state.definitionsIndex[definitionId]["alias"];
          } else {
            execution["definition_alias"] = null;
          }
        }

        // Clear progress
        if ( !silent ) {
          this.updateProgress(false, false, null);
        }

        // Merge results with previously loaded ones in case of paging
        if ( more ) {
          executions.push(...this.state.executions);
        }

        // Update component state
        this.setState({
          session: session,
          executions: executions,
          lastExecutionId: lastExecutionId
        });

        // Resolve who ran each of them. Asynchronous and deliberately not awaited: the rows render
        // immediately, and the Caller column fills in as the lookups land.
        this.resolveCallers(executions);

        // Hand the record up so the breadcrumb can label the session by name without its own fetch.
        // Only on an actual change: the status re-poll runs this every 2s.
        this.reportSession(session);

        return { session: session };
      }, error => {
        // Display failure message
        if ( !silent ) {
          this.updateProgress(false, true, "Failed to fetch the list of executions: " + error.message);
        }
        return { error: error };
      });
  }

  reportSession(session) {
    if ( !this.props.onSessionLoaded || !session ) {
      return;
    }
    const label = Utils.aliasOrId(session);
    if ( label === this.reportedSessionLabel ) {
      return;
    }
    this.reportedSessionLabel = label;
    this.props.onSessionLoaded(session);
  }

  /**
   * Resolves the principal behind each execution's `user_id` and attaches the record to its row, the
   * way `definition_alias` is attached in loadExecutions.
   *
   * Resolution belongs here rather than in the cell renderer: `Api.usersIdGet` memoises what it has
   * already resolved, but does not dedupe requests still in flight, so a renderer that fetched its own
   * user would fire one request per row on first paint.
   */
  resolveCallers(executions) {
    const pending = new Set();
    for ( const execution of ( executions || [] ) ) {
      const userId = execution["user_id"];
      if ( userId && !(userId in this.callersById) ) {
        pending.add(userId);
      }
    }

    if ( 0 === pending.size ) {
      this.attachCallers();
      return Promise.resolve();
    }

    // A FAILURE IS NOT REMEMBERED. Writing `null` here put the id in the map, so the `in` check above
    // treated it as resolved and never asked again — one transient rejection on mount (a token being
    // refreshed, a blip) left every row showing a raw uuid for the life of the view, which is exactly
    // the "force refresh makes the names appear" shape: a reload is the only thing that clears it.
    // Retrying costs one request per load per unresolvable id, which is the cheaper failure.
    return Promise.all([...pending].map(userId =>
      this.props.app.api.usersIdGet(userId).then(
        response => { this.callersById[userId] = response.data.user; },
        () => { delete this.callersById[userId]; })
    )).then(() => this.attachCallers());
  }

  /**
   * Republishes the rows with their callers attached. A new array and new row objects, so the grid sees
   * changed data and repaints the column.
   */
  attachCallers() {
    // Functional, because `this.state` is not the state this is about to land on. `loadExecutions`
    // calls `setState({executions})` and then calls in here synchronously; React batches, so reading
    // `this.state.executions` returns the array from BEFORE the load and republishes it — reverting
    // the grid to the previous page's rows. Only visible when every caller is already cached, which
    // is the ordinary case from the second load onwards.
    this.setState(state => (
      state.executions
        ? {
            executions: state.executions.map(execution => ({
              ...execution,
              caller: this.callersById[execution["user_id"]] || null
            }))
          }
        : null
    ));
  }

  createExecution(executionDescription) {
    // Render progress
    this.updateProgress(true, false, "Creating a new execution...");

    // Create execution
    const sessionId = this.props.match.params.sectionId;
    const body = {
      "engine": "workflow_blobhub",
      "command": "create_execution",
      "session_id": sessionId,
      "definition_id": executionDescription.definition["id"],
      "input_data": executionDescription.inputData,
      "tracing": executionDescription.tracing
    };
    if (executionDescription.description && executionDescription.description.trim()) {
      body["description"] = executionDescription.description.trim();
    }
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        const executionId = response.data["execution"]["id"];

        // Navigate to newly created execution
        this.proceedToExecution(executionId);
      }, error => {
        // Render error state
        this.updateProgress(false, true, "Failed to create execution: " + error.message);
      });
  }

  /**
   * Session lifecycle (close / reopen / delete). Calls the command channel directly, the same local
   * pattern createExecution() above uses — this view does not go through the playground session
   * manager. `sessionActionBusy` is a separate flag from `progress`/`error`: setting `progress` true
   * would hide the whole Actions section (see the `!this.state.progress` gate around it in render()),
   * but the buttons need to stay visible while disabled/relabeled during the request.
   */

  closeSession() {
    this.setState({ sessionActionBusy: "close" });

    const sessionId = this.props.match.params.sectionId;
    const body = {
      "engine": "workflow_blobhub",
      "command": "close_session",
      "session_id": sessionId
    };
    return this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        const session = response.data["session"];
        this.updateProgress(false, false, null);
        this.setState({ session: session, sessionActionBusy: null });
        this.pollWhileTransitioning(session);
      }, error => {
        this.setState({ sessionActionBusy: null });
        this.updateProgress(false, true, "Failed to close session: " + Utils.errorMessage(error));
      });
  }

  reopenSession() {
    this.setState({ sessionActionBusy: "reopen" });

    const sessionId = this.props.match.params.sectionId;
    const body = {
      "engine": "workflow_blobhub",
      "command": "reopen_session",
      "session_id": sessionId
    };
    return this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        const session = response.data["session"];
        this.updateProgress(false, false, null);
        this.setState({ session: session, sessionActionBusy: null });
        // Reopen lands on "open" synchronously, so there is nothing to wait for — but route it
        // through the same guard so an unexpected transitional status still gets followed.
        this.pollWhileTransitioning(session);
      }, error => {
        this.setState({ sessionActionBusy: null });
        this.updateProgress(false, true, "Failed to reopen session: " + Utils.errorMessage(error));
      });
  }

  deleteSession() {
    this.setState({ sessionActionBusy: "delete" });

    const sessionId = this.props.match.params.sectionId;
    const body = {
      "engine": "workflow_blobhub",
      "command": "delete_session",
      "session_id": sessionId
    };
    return this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        const session = response.data["session"];
        this.updateProgress(false, false, null);
        this.setState({ session: session, sessionActionBusy: null });
        this.pollWhileTransitioning(session);
      }, error => {
        this.setState({ sessionActionBusy: null });
        this.updateProgress(false, true, "Failed to delete session: " + Utils.errorMessage(error));
      });
  }

  /**
   * Updates the session — the application's first update_session call site. All three fields
   * (alias, description, tracing) are sent, since CreateSessionModal's edit mode makes every field
   * editable. The alias is the one exception: it is omitted entirely when blank, because the server
   * grammar rejects "" and a blank field means "leave the alias unchanged", not "clear it".
   *
   * Resolves rather than rejects: a 400 alias_in_use is about the value typed into the modal, so the
   * modal owns the message and this must not raise the page-level error banner. Every other failure
   * (e.g. 409 session_not_open, which now gates every field, not just the alias) comes back with the
   * backend's own explanation (Utils.errorMessage) for the modal to show as a general error.
   */
  updateSession(fields) {
    this.setState({ sessionActionBusy: "edit" });

    const sessionId = this.props.match.params.sectionId;
    const body = {
      "engine": "workflow_blobhub",
      "command": "update_session",
      "session_id": sessionId,
      "description": ( fields.description || "" ).trim(),
      "tracing": fields.tracing
    };
    const alias = ( fields.alias || "" ).trim();
    if ( alias ) {
      body["alias"] = alias;
    }
    return this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        const session = response.data["session"];
        this.setState({ session: session, sessionActionBusy: null });
        this.reportSession(session);
        return { ok: true };
      }, error => {
        this.setState({ sessionActionBusy: null });
        if ( this.isAliasInUse(error) ) {
          return { ok: false, aliasInUse: true };
        }
        return { ok: false, message: Utils.errorMessage(error) };
      });
  }

  // A collision is HTTP 400 with error="alias_in_use" in the body. The message is checked too so a
  // body that carries only `message` still lands on the field rather than reading as a generic
  // failure.
  isAliasInUse(error) {
    const data = error?.response?.data;
    if ( error?.response?.status !== 400 ) {
      return false;
    }
    return data?.error === "alias_in_use" || /alias_in_use/.test(data?.message || "");
  }

  /**
   * Live status on the actions surface.
   *
   * close_session and delete_session are asynchronous: they answer with "closing" / "deleting" and
   * the backend settles seconds later. This view owns Close/Reopen/Delete but has no session
   * manager and therefore no event stream, so without a re-poll it never learns the transition
   * finished — Close sits disabled and relabelled "Closing…", Delete disabled, leaving no available
   * action and no hint indefinitely; a delete is worse still, pinning at "deleting" while the page
   * renders a session that no longer exists.
   *
   * The poll re-reads the session through the view's existing loadExecutions(include_session: true)
   * path in silent mode, keeps going only while the status is transitional, and is bounded by
   * SESSION_POLL_MAX_ATTEMPTS so it can never spin for the life of the page.
   */

  pollWhileTransitioning(session) {
    if ( isSessionTransitioning(session) ) {
      this.startSessionStatusPoll();
    }
  }

  startSessionStatusPoll() {
    // Never schedule for a view that is not (or no longer) mounted.
    if ( !this.mounted ) return;

    this.stopSessionStatusPoll();
    this.sessionPollAttempts = 0;
    this.scheduleSessionStatusPoll();
  }

  scheduleSessionStatusPoll() {
    this.sessionPollTimer = setTimeout(() => {
      this.sessionPollTimer = null;
      if ( !this.mounted ) return;
      this.pollSessionStatus();
    }, SESSION_POLL_INTERVAL_MS);
  }

  stopSessionStatusPoll() {
    if ( this.sessionPollTimer ) {
      clearTimeout(this.sessionPollTimer);
      this.sessionPollTimer = null;
    }
  }

  pollSessionStatus() {
    this.sessionPollAttempts++;

    return this.loadExecutions(false, true).then( result => {
      if ( !this.mounted ) return;

      // The delete finished. Nothing here is meaningful any more, so leave for the sessions list.
      if ( this.isSessionGone(result) ) {
        this.stopSessionStatusPoll();
        this.proceedToSessions();
        return;
      }

      if ( !result.error ) {
        const status = result.session ? result.session["status"] : null;
        if ( status !== SESSION_STATUS.CLOSING && status !== SESSION_STATUS.DELETING ) {
          // Terminal for this poll — "closed" (or back to "open"). Nothing left to wait for.
          this.stopSessionStatusPoll();
          return;
        }
      }

      // Still transitional, or a transient read failure worth retrying — within the bound.
      if ( this.sessionPollAttempts >= SESSION_POLL_MAX_ATTEMPTS ) {
        this.stopSessionStatusPoll();
        return;
      }
      this.scheduleSessionStatusPoll();
    });
  }

  /**
   * Whether the poll's read says the session no longer exists.
   *
   * Verified against the backend (api/storage/workflow.py): list_executions fetches the session and
   * calls WorkflowAccess.validate_revision_reference(revision, item=session) unconditionally, before
   * it ever looks at include_session. That helper raises AccessException -> 403 whenever the session
   * row is missing (api/permissions/workflow.py). So once a delete completes, this poll's read
   * REJECTS with 403 — it never resolves 200 with an absent `session`; a success carrying no session
   * is not a wire shape the backend can produce here, so it is not read as "gone" either.
   *
   * A 403 could in principle also mean the caller lost permission mid-poll rather than the session
   * being deleted, but within this delete/close status-poll, navigating back to the sessions list is
   * the correct, safe outcome either way, so no further disambiguation is needed. Every other
   * failure — network blip, 5xx — is transient and must NOT be read as deletion, or a flaky
   * connection would navigate the user off a perfectly live session.
   */
  isSessionGone(result) {
    return result.error?.response?.status === 403;
  }

  proceedToSessions() {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    this.props.history.push(Navigation.workflowPath(orgId, blobId, "debugger"));
  }

  /**
   * Close/Delete confirmation gating. Close and Delete are consequential (they stop executions /
   * permanently destroy data), so SessionActions' onClose/onDelete arm `confirmAction` instead of
   * dispatching straight away; ConfirmModal renders when it is set, and only its own Yes
   * button calls handleConfirmLifecycleAction(), which performs the actual dispatch. Reopen is
   * benign and keeps dispatching immediately (its SessionActions prop is wired straight to
   * reopenSession(), same as Task 3) — it never touches confirmAction.
   */

  requestConfirmClose() {
    this.setState({ confirmAction: "close" });
  }

  requestConfirmDelete() {
    this.setState({ confirmAction: "delete" });
  }

  dismissConfirmLifecycle() {
    this.setState({ confirmAction: null });
  }

  handleConfirmLifecycleAction() {
    if ( this.state.confirmAction === "close" ) {
      return this.closeSession();
    }
    if ( this.state.confirmAction === "delete" ) {
      return this.deleteSession();
    }
  }

  // Title/body/confirm-label for whichever action is currently armed. Body copy states the
  // consequences up front (Close: stops executions, read-only, reopenable; Delete: permanent,
  // removes everything) and names the session, mirroring the shared ConfirmModal's
  // "Are you sure ... <strong>{name}</strong>?" phrasing convention used at every call site.
  sessionConfirmConfig() {
    const session = this.state.session;
    // A chosen name renders as the shared pill; a UUID stays bold text — it is a value being read,
    // not a name. Both keep the shared ConfirmModal's "…<strong>{name}</strong>?" phrasing shape.
    const sessionName = ( session && session.alias )
      ? <Alias value={session.alias} />
      : <strong>{session ? session.id : null}</strong>;

    if ( this.state.confirmAction === "close" ) {
      return {
        title: "Close Session",
        body: (
          <>
            Are you sure that you would like to close session {sessionName}? Closing stops any
            running executions and makes the session read-only; it can be reopened later.
          </>
        ),
        confirmLabel: "Yes"
      };
    }

    if ( this.state.confirmAction === "delete" ) {
      return {
        title: "Delete Session",
        body: (
          <>
            Are you sure that you would like to delete session {sessionName}? Deletion is permanent
            and cannot be undone; it removes the session and all of its objects, threads, graphs, and executions.
          </>
        ),
        confirmLabel: "Yes"
      };
    }

    return { title: "", body: null, confirmLabel: "Yes" };
  }

  proceedToExecution(executionId) {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    const sessionId = this.props.match.params.sectionId;
    this.props.history.push(Navigation.workflowPath(orgId, blobId, "debugger", sessionId, executionId));
  }

  render() {
    const columns = [
      {
        "field": "id",
        "headerName": "Execution ID",
        "filter": true,
        "flex": 6,
        "sortable": false,
        cellRenderer: params => {
          return (
            <a href="#" onClick={(event) => {
              event.preventDefault();
              this.proceedToExecution(params.value);
            }}>{params.value}</a>
          );
        }
      },
      {
        "field": "created_at",
        "headerName": "Created",
        "filter": false,
        "flex": 6,
        "sortable": true,
        "sort": "desc",
        cellRenderer: params => {
          return ( <> { Utils.formatCompactDateTime(params.value, this.props.timezone) } </> );
        }
      },
      {
        // Beside Created, because when it started and how long it took read together. The VALUE is
        // milliseconds (see durationValueGetter) so sorting orders by time rather than by text, and
        // so a running execution's cell repaints as the elapsed time grows. Not filterable: nobody
        // types a duration into a filter box.
        "field": "ended_at",
        "headerName": "Duration",
        "filter": false,
        "flex": 3,
        "sortable": true,
        valueGetter: durationValueGetter,
        cellRenderer: durationCellRenderer
      },
      {
        "field": "definition_alias",
        "headerName": "Definition",
        "filter": true,
        "flex": 4,
        "sortable": true,
        cellRenderer: params => {
          if ( !params.value ) {
             return ( <Badge className="title-tag-badge-warning">not found</Badge> );
          }
          return ( <> {params.value} </> );
        }
      },
      {
        // The VALUE is the caller's display name, not the raw id, for the reason the column exists at
        // all: AG Grid repaints a cell when its value changes, and `resolveCallers` attaches `caller`
        // to the row without touching `user_id`. Read the id and the cell never repaints — it holds
        // the first paint's fallback, the raw uuid, for ever. Sorting and filtering follow the name
        // for free, which is what someone typing in that filter box means.
        "field": "user_id",
        "headerName": "Caller",
        "filter": true,
        "flex": 6,
        "sortable": true,
        valueGetter: callerValueGetter,
        cellRenderer: callerCellRenderer
      },
      {
        // Widths are a judgement about the CONTENT, not the header: a description is optional and
        // usually absent, so this was the widest column in the grid while showing "No description"
        // on every row, and the id, caller and duration beside it all truncated. It keeps enough
        // room to be readable when there IS one.
        "field": "description",
        "headerName": "Description",
        "filter": true,
        "flex": 5,
        "sortable": true,
        cellRenderer: params => {
          return (<>{params.value || <span className="text-muted">No description</span>}</>);
        }
      },
      {
        "field": "status",
        "headerName": "Status",
        "filter": true,
        "flex": 3,
        "sortable": true,
        cellRenderer: params => {
          return ( <Badge className="title-tag-badge">{params.value}</Badge> );
        }
      }
    ];

    return (
      <>
        {( this.state.progress || this.state.error ) &&
          <Container>
            <Row>
              <Col>
                <Form.Group>
                  <Alert variant={this.state.progress ? "primary" : "warning"}>
                    {this.state.message}
                  </Alert>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        }
        <Container className={this.state.details ? "container-wide" : ""}>
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  Executions
                  <span className="float-right">
                    {this.state.executions &&
                      <>
                        Total fetched: <strong>{this.state.executions.length}</strong>
                        {this.state.lastExecutionId &&
                          <> | More available</>
                        }
                      </>
                    }
                    {!this.state.details &&
                      <ButtonGroup size="sm" className="header-options">
                        <Button variant="light" onClick={() => { this.setState({ details: true }) }}><GoInfo/></Button>
                      </ButtonGroup>
                    }
                  </span>
                </Card.Header>
                <Card.Body>
                  <Card.Text>
                    <div
                      className="ag-theme-quartz"
                    >
                      <AgGridReact
                        rowData={this.state.executions}
                        columnDefs={columns}
                        domLayout='autoHeight'
                        pagination={false}
                      />
                    </div>
                  </Card.Text>
                  {!this.state.progress &&
                    <Card.Text>
                      {this.state.lastExecutionId &&
                        <Button variant="light" size="sm" className="mr-3" onClick={() => {
                          this.loadExecutions(true); 
                        }}><MdOutlineExpandMore /> Load More Results</Button>
                      }
                      <Button variant="light" size="sm" onClick={() => {
                        this.loadExecutions();
                      }}><MdRefresh /> Refresh</Button>
                    </Card.Text>
                  }
                </Card.Body>
              </Card>
            </Col>
            <Col sm={4} className={`detail-panel-col ${this.state.details ? "" : "hidden"}`}>
              <Card>
                <Card.Header>
                  Overview
                  <span className="float-right">
                    <ButtonGroup size="sm" className="header-options">
                      <Button variant="light" onClick={() => { this.setState({ details: false }) }}><GoX/></Button>
                    </ButtonGroup>
                  </span>
                </Card.Header>
                <Card.Body>
                  <Card.Text as="h6">General</Card.Text>
                  <hr />
                  <Card.Text>
                    Execution runs a workflow of interconnected processors, each performing specific tasks.
                    It happens within a session, maintaining shared state throughout.
                  </Card.Text>

                  {this.state.session &&
                    <>
                      <Card.Text as="h6" className="d-flex justify-content-between align-items-center bh-alias-fit">
                        Session
                        <Clipboard text={JSON.stringify(this.state.session, null, 2)} />
                      </Card.Text>
                      <hr />
                      <Card.Text>
                        <SessionProperties
                          session={this.state.session}
                          timezone={this.props.timezone}
                        />
                      </Card.Text>
                    </>
                  }

                  {(!this.state.progress && this.props.canEditRevision ) &&
                    <>
                      <Card.Text as="h6">Actions</Card.Text>
                      <ActionCards>
                        {( this.state.definitions && this.state.definitions.length > 0 ) ?
                          // New Execution starts a run (create_execution) — a session-data mutation —
                          // so it additionally requires the session to be open (Task 5).
                          ( isSessionOpen(this.state.session) ?
                            <ActionCard helper="Creates a new execution within the session">
                              <Button variant="success" size="sm" onClick={() => {
                                this.setState({ createExecutionModelShow: true });
                              }}><IoIosPlay /> New Execution</Button>
                            </ActionCard>
                            : <ActionCard helper={SESSION_CLOSED_HINT}>
                              <Button variant="success" size="sm" disabled>
                                <IoIosPlay /> New Execution
                              </Button>
                            </ActionCard>
                          )
                          : <ActionCard helper="Create a workflow definition first">
                            <Button variant="success" size="sm" disabled>
                              <IoIosPlay /> New Execution
                            </Button>
                          </ActionCard>
                        }

                        <SessionActions
                          session={this.state.session}
                          canEdit={this.props.canEditRevision}
                          busy={!!this.state.sessionActionBusy}
                          onClose={() => this.requestConfirmClose()}
                          onReopen={() => this.reopenSession()}
                          onDelete={() => this.requestConfirmDelete()}
                          onEdit={() => this.setState({ editSessionModalShow: true })}
                        />
                      </ActionCards>
                    </>
                  }
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        <CreateExecutionModal
          app={this.props.app}
          revision={this.props.revision}
          definitions={this.state.definitions}
          show={this.state.createExecutionModelShow}
          onHide={() => this.setState({ createExecutionModelShow: false })}
          handleCreate={(execution) => this.createExecution(execution)}
        />

        {this.state.session &&
          <CreateSessionModal
            session={this.state.session}
            show={this.state.editSessionModalShow}
            onHide={() => this.setState({ editSessionModalShow: false })}
            handleCreate={(fields) => this.updateSession(fields)}
          />
        }

        <ConfirmModal
          show={!!this.state.confirmAction}
          onHide={() => this.dismissConfirmLifecycle()}
          onConfirm={() => this.handleConfirmLifecycleAction()}
          {...this.sessionConfirmConfig()}
        />
      </>
    );
  }
}
